import { AxiosError } from 'axios';
import { TableActionTypes } from 'web-library';
import axiosClient from '../axiosClient';
import { POST_GROUP_EXAM_REGISTRATION_SUMMARY } from '../../constants/apiUrls';
import { useCustomMutation } from '../utils/useCustomMutation';
import { ExamGroupExamLevelFeeResponse, ExamPayment, GenericErrorResponse } from '../typings';
import { PaginatedData } from '../../models/List';
import { ApiPathParam } from '../../models/SearchParamSchema';
import { CandidateList, SubjectList } from './usePostReviewGroupExamRegistration';
import { ExamRegistrationType } from '../../api/candidate/exam/model';

// =============================================================================
// Request & Response Model
// =============================================================================
export enum RegistrationRequestTab {
  CANDIDATE_LIST = 'candidateList',
  SUBJECT_LIST = 'subjectList',
}

export interface PostGroupExamRegistrationSummaryRequest {
  applicationUuid: string;
  apiPathParam: ApiPathParam;
  tab: RegistrationRequestTab;
  paymentStatus: string;
}

export enum ExamPaymentModeKey {
  bankTransferOption = 'bankTransferOption',
  candidatePayOption = 'candidatePayOption',
  coordinatorPayOption = 'coordinatorPayOption',
  noPaymentModeSelected = 'noPaymentModeSelected',
}

export interface ExamPaymentMode {
  [ExamPaymentModeKey.bankTransferOption]: boolean;
  [ExamPaymentModeKey.candidatePayOption]: boolean;
  [ExamPaymentModeKey.coordinatorPayOption]: boolean;
  [ExamPaymentModeKey.noPaymentModeSelected]?: boolean;
}

export interface TransactionDetails {
  transactionNo: string;
  internalTransactionRef: string;
  paymentStatus: string;
}

export interface SummaryCandidateList extends CandidateList {
  paymentStatus: string;
  paymentLink: boolean;
}

export interface GroupExamRegistrationSummaryBaseResponse {
  applicationId: string;
  applicationStatus: string;
  applicationDateTime: string;
  coordinatorName: string;
  examLevel: string[];
  examYear: number;
  examRegistrationType: ExamRegistrationType;
  groupedLevelFees: ExamGroupExamLevelFeeResponse[];
  payment: ExamPaymentMode & ExamPayment & TransactionDetails;
  gstRegNo?: string | null;
}

export interface PostGroupExamRegistrationSummaryResponse
  extends GroupExamRegistrationSummaryBaseResponse {
  subjectList?: PaginatedData<SubjectList[]>;
  candidateList?: PaginatedData<SummaryCandidateList[]>;
}

// =============================================================================
// Mutation
// =============================================================================
interface Input {
  onSuccess?: () => void;
  defaultOnSuccessInput: {
    getCandidateOrSubjectType: RegistrationRequestTab;
    tableDispatch: React.Dispatch<TableActionTypes>;
  };
  onError?: (data: AxiosError<GenericErrorResponse>) => void;
  onSettled?: () => void;
}

export const usePostGroupExamRegistrationSummary = ({
  onSuccess,
  onError,
  onSettled,
  defaultOnSuccessInput,
}: Input) => {
  const { getCandidateOrSubjectType, tableDispatch } = defaultOnSuccessInput;
  const postGroupExamRegistrationSummary = async (
    payload: PostGroupExamRegistrationSummaryRequest
  ) => {
    const res = await axiosClient.post<PostGroupExamRegistrationSummaryResponse>(
      POST_GROUP_EXAM_REGISTRATION_SUMMARY(payload.applicationUuid, payload.apiPathParam),
      {
        tab: payload.tab,
        paymentStatus: payload.paymentStatus,
      }
    );
    return res.data;
  };

  return useCustomMutation(postGroupExamRegistrationSummary, {
    onSuccess: (data: PostGroupExamRegistrationSummaryResponse) => {
      const paginatedData = data[getCandidateOrSubjectType] as PaginatedData<CandidateList[]>;
      tableDispatch(({
        type: 'UPDATE_DATA',
        payload: {
          data: paginatedData.content,
          count: paginatedData.totalElements,
        },
      } as unknown) as TableActionTypes);
      onSuccess?.();
    },
    onError,
    onSettled,
  });
};
